import { getCookie, setStrictlyNecessaryCookie } from '@atlassian/browser-storage-controls';
import { GraphQLError } from 'graphql';

import { getConfig, getEnv, isEmbedOrIntegration, isInIframe } from '@townsquare/config';
import { location, redirect } from '@townsquare/facade/window';
import { logger } from '@townsquare/logging/logger';
import { ORG_SITE_ROUTE_REGEX } from '@townsquare/router/utils';

const SAMPLE_DIGEST_EMAIL_ORIGIN_ID = 'sample-origin-id';

const config = getConfig();

export function returnAfterLogin() {
	if (isEmbedOrIntegration() || isInIframe()) {
		// Embeds and Jira Integration needs to handle this differently
		return;
	}
	redirect(getLogInUrl());
}

export const logout = () => {
	const currentUrl = new URL(location().href);
	currentUrl.searchParams.delete('cloudId');
	const currentUrlWithCloudIdParamRemoved = currentUrl.toString();
	redirect(`${config.atlassianAccountUrl}/logout?continue=${currentUrlWithCloudIdParamRemoved}&application=atlas`);
};

export function getLogInUrl(redirectUrl?: string) {
	const href = redirectUrl || location().href;

	const url = new URL(href);

	// Extract orgId from pathname pattern: /o/{orgId}/...
	const orgId = url.pathname.match(ORG_SITE_ROUTE_REGEX)?.groups?.orgIdentifier;

	const loginUrl = new URL(`${config.atlassianAccountUrl}/login`);
	loginUrl.searchParams.set('continue', href);
	loginUrl.searchParams.set('application', 'atlas');
	if (orgId) {
		loginUrl.searchParams.set('orgId', orgId);
	}

	if (getEnv() === 'local') {
		loginUrl.searchParams.set('dstDomainOverride', config.homeUrl.replace('https://', ''));
	}

	return loginUrl.toString();
}

export function isUsingDefaultUrl() {
	if (config.env === 'stg-east' || config.env === 'prod-east') {
		const currentUrl = location().href;
		const mainUrl = config.fullUrl;

		return currentUrl.startsWith(mainUrl);
	} else {
		return true;
	}
}

export enum StorageAccessState {
	Granted,
	UserMustGrant,
	Unknown,
}

const jiraIntegrationCookie = 'com.atlassian.atlas.jira-integration-cookie-check';

const checkCanSetCookieForJiraIntegration = (): boolean => {
	try {
		setStrictlyNecessaryCookie(jiraIntegrationCookie, 'any', {
			sameSite: 'None',
			secure: true,
			expires: new Date(0), // Thu, 01 Jan 1970 00:00:00 GMT
		});
		const cookieThatWasSet = getCookie(jiraIntegrationCookie);

		return !!cookieThatWasSet;
	} catch {
		return false;
	}
};

export async function requestStorageAccess(
	// We can only call document.requestStorageAccess in the context of a user interaction
	isUserRequest?: boolean,
): Promise<StorageAccessState> {
	if (checkCanSetCookieForJiraIntegration()) {
		// Do not need storage access if we can set cookies, because that's what the Storage Access API is an alternative for
		return StorageAccessState.Granted;
	}

	if (document.hasStorageAccess) {
		const hasAccess = await document.hasStorageAccess();
		if (!hasAccess) {
			// Check whether unpartitioned cookie access has been granted
			// to another same-site embed
			try {
				const permission = await navigator.permissions.query({
					// 'storage-access' only exists in some browsers,  but we cannot assume which do: https://developer.mozilla.org/en-US/docs/Web/API/Permissions
					name: 'storage-access' as PermissionName & 'storage-access',
				});

				if (permission.state === 'granted' || isUserRequest) {
					// If so, you can just call requestStorageAccess() without a user interaction,
					// and it will resolve automatically.
					await document.requestStorageAccess();
					return StorageAccessState.Granted;
				} else if (permission.state === 'prompt') {
					// Need to call requestStorageAccess() after a user interaction
					return StorageAccessState.UserMustGrant;
				} else if (permission.state === 'denied') {
					// User has denied unpartitioned cookie access
					logger.warning('User has denied unpartitioned cookie access');
					return StorageAccessState.UserMustGrant;
				}
			} catch (error) {
				// Some browsers don't support this API, so we can't assume it will work
				return StorageAccessState.Granted;
			}
		}
	}
	return StorageAccessState.Granted;
}

export function redirectToHowPageFromSampleEmail(atlOrigin?: string) {
	if (atlOrigin && atlOrigin === SAMPLE_DIGEST_EMAIL_ORIGIN_ID) {
		redirect('/how');
	}
}

export function isUnauthorizedError(graphQLErrors?: ReadonlyArray<GraphQLError>) {
	if (graphQLErrors && graphQLErrors.length > 0) {
		const errText = graphQLErrors[0]?.message;

		if (
			errText?.includes('401 UNAUTHORIZED') ||
			errText?.includes('403 FORBIDDEN') ||
			errText?.includes('Access is denied')
		) {
			return true;
		}
	}

	return false;
}
